import { runInAction, makeAutoObservable, action } from 'mobx';
import { State } from '~/store/types';
import { AlertPriorityOrder } from '@watcherr/resources';
import { sameOff, startOfDay, startOf, alertGradients, alertColors } from '@watcherr/ui';
import { AlertActivityChartFilter } from '~config/dashboard';
import { endOfDay, subMinutes } from 'date-fns';
import AlertStore from '~store/Alert/Alert';

export const graphConfig = {
  pastHour: { minutesOff: 60, per: 'minute' },
  pastDay: { minutesOff: 60 * 24, per: 'hour' },
  past7days: { minutesOff: 7 * 60 * 24, per: 'day' },
  pastMonth: { minutesOff: 31 * 60 * 24, per: 'day' },
  past2weeks: { minutesOff: 14 * 60 * 24, per: 'day' },
};

const getDefaultData = (filter: AlertActivityChartFilter): any[] => {
  const types = AlertPriorityOrder;

  return types.map((type) => {
    const dataPoints: { x: Date; y: number }[] = [];

    const { per, minutesOff } = graphConfig[filter];
    let loops = minutesOff;
    if (per === 'hour') {
      loops /= 60;
    } else if (per === 'day') {
      loops /= 60 * 24;
    }
    for (let i = 0; i < loops; i++) {
      let date = new Date(new Date().getTime() - i * 1000 * 60);
      if (per === 'hour') {
        date = new Date(new Date().setMinutes(0, 0, 0) - i * 1000 * 60 * 60);
      } else if (per === 'day') {
        date = startOfDay(new Date(new Date().setHours(0, 0, 0) - i * 1000 * 60 * 60 * 24));
      }
      dataPoints.push({
        x: date,
        y: 0,
      });
    }
    return {
      gradient: alertGradients[type],
      id: type,
      color: alertColors[type],
      dataPoints,
    };
  });
};

class AlertActivityChartStore {
  rootStore: State;
  alarms: AlertStore[] = [];
  filter: AlertActivityChartFilter = 'past7days';
  loading: boolean = true;
  tooltipWithTimestamp = false;

  static readonly MAX_SAFE_ALERTS = 50;

  constructor(rootStore: State) {
    makeAutoObservable(this, {
      filterChanged: action.bound,
      loadData: action.bound,
      rootStore: false,
    });
    this.rootStore = rootStore;
  }

  get filteredAlarms() {
    const { minutesOff, per } = graphConfig[this.filter] || {};

    let checkDate = minutesOff && new Date().getTime() - minutesOff * 1000 * 60;

    if (per === 'day') {
      checkDate = minutesOff && new Date().setHours(23, 59, 59) - minutesOff * 1000 * 60;
    }

    const alarms = checkDate ? this.alarms.filter((alarm) => alarm.created.getTime() > checkDate) : this.alarms;
    return alarms;
  }

  get lines() {
    const lines = this.filteredAlarms
      .map((alarm) => {
        return {
          date: startOf(alarm.created, graphConfig[this.filter].per),
          type: alarm.priority,
        };
      })
      .reduce((grouped, { type, date }) => {
        const include = grouped.find((item) => item.id === type);
        if (!include) {
          grouped.push({
            gradient: alertGradients[type],
            id: type,
            color: alertColors[type],
            dataPoints: [
              {
                x: date,
                y: 1,
              },
            ],
          });
        } else {
          const dateObject = include.dataPoints.find((d) => sameOff(d.x, date, graphConfig[this.filter].per));
          if (dateObject) {
            dateObject.y += 1;
          } else {
            include.dataPoints.push({
              x: date,
              y: 1,
            });
          }
        }
        return grouped;
      }, getDefaultData(this.filter));
    return lines;
  }
  filterChanged(filter: AlertActivityChartFilter) {
    this.filter = filter;
    this.tooltipWithTimestamp = ['hour', 'minute'].includes(graphConfig[this.filter].per);
    this.loadData();
  }

  loadData() {
    this.loading = true;
    const minutesToLoad = graphConfig[this.filter].minutesOff;
    const end = endOfDay(new Date()).valueOf();
    const start = startOfDay(subMinutes(end, minutesToLoad));
    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const numberOfDaysAgo = Math.round((end - start) / millisecondsPerDay);
    const forceFetch = this.alarms.length < AlertActivityChartStore.MAX_SAFE_ALERTS;
    this.rootStore.alerts
      .fetchLatestAlerts({ numberOfDaysAgo, forceFetch })
      .then(() => {
        runInAction(() => {
          this.alarms = this.rootStore.alerts.alertsSorted;
          this.loading = false;
        });
      })
      .catch((error) => {
        runInAction(() => {
          this.loading = false;
          console.error('Failed to load alert activity data', error);
        });
      });
  }
}

export default AlertActivityChartStore;
